import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../css/Map.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { db } from '../../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { Form, Table } from 'react-bootstrap';
import { TbRectangleVerticalFilled } from "react-icons/tb";
import { FaChevronUp, FaChevronDown, FaDirections } from "react-icons/fa";
import { GoTriangleUp, GoTriangleDown } from "react-icons/go";
import FilterModal from '../components/FilterModal';

// Calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return parseFloat((R * c).toFixed(1));
};

// Map component
const Map = () => {
    const [position, setPosition] = useState([45.4215, -75.6972]); // default to Ottawa
    const [userPosition, setUserPosition] = useState(null);
    const [entities, setEntities] = useState([]);
    const [filterType, setFilterType] = useState("All");
    const [filterSpecification, setFilterSpecification] = useState("All");
    const [sortOrder, setSortOrder] = useState("asc"); // For sorting beds left
    const [selectedEntity, setSelectedEntity] = useState(null); // Track the selected entity
    const [sortConfig, setSortConfig] = useState({ key: 'distance', direction: 'asc' });
    const [zoom, setZoom] = useState(12); // 默认 zoom 级别
    const navigate = useNavigate();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const [filteredEntities, setFilteredEntities] = useState([]);

    const defaultIcon = L.icon({
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [0, -41],
    });

    const fetchData = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "entities"));
            const data = querySnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(entity => entity.address && entity.address.latitude && entity.address.longitude);

            setEntities(data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const { latitude, longitude } = pos.coords;
                    setUserPosition([latitude, longitude]);
                    setPosition([latitude, longitude]);
                },
                () => {
                    console.error('Geolocation not available or permission denied');
                }
            );
        }
    }, []);

    const handleEntityClick = (id) => {
        navigate(`/entityInfo?entityId=${id}`);
    };

    const MapCenter = ({ latitude, longitude, zoom }) => {
        const map = useMap();
        useEffect(() => {
            map.setView([latitude, longitude], zoom || map.getZoom(), { animate: true });
        }, [latitude, longitude, zoom, map]);
        return null;
    };

    const handleTableEntityClick = (latitude, longitude, entity) => {
        setPosition([latitude, longitude]);
        setSelectedEntity(entity);
        setZoom(14);
    };

    const handleEntityNameClick = (id) => {
        navigate(`/entityInfo?entityId=${id}`);
    };

    const toggleSortOrder = () => {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    };

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const computedFilteredEntities = useMemo(() => {
        if (!activeFilters || Object.keys(activeFilters).length === 0) {
            return entities;
        }
        return entities.filter(entity => {
            // 获取第一个服务对象进行过滤
            const service = entity.services[0];
            if (!service) return false;

            // 服务时间过滤
            if (activeFilters.serviceHours?.length > 0) {
                const entityHours = service.hours_of_operation?.map(h => h.category) || [];
                if (!activeFilters.serviceHours.some(hour => entityHours.includes(hour))) {
                    return false;
                }
            }

            // 语言过滤
            if (activeFilters.languages?.length > 0) {
                if (!activeFilters.languages.some(lang =>
                    service.languages?.includes(lang)
                )) {
                    return false;
                }
            }

            // 资源过滤
            if (activeFilters.hasAvailableBeds && !service.resources?.beds?.available) {
                return false;
            }
            if (activeFilters.hasFood && !service.resources?.food?.available) {
                return false;
            }
            if (activeFilters.hasWellness && !service.resources?.wellness?.available) {
                return false;
            }
            if (activeFilters.hasOtherResources && !service.resources?.other_resources?.available) {
                return false;
            }

            // 健康资源过滤
            if (activeFilters.healthResources?.available && !service.resources?.health_resources?.available) {
                return false;
            }
            if (activeFilters.healthResources?.requiresOHIP && !service.resources?.health_resources?.requires_ohip) {
                return false;
            }
            if (activeFilters.healthResources?.requiresID && !service.resources?.health_resources?.requires_id) {
                return false;
            }

            // 心理健康支持过滤
            if (activeFilters.mentalHealthSupport?.available && !service.resources?.mental_health_support?.available) {
                return false;
            }
            if (activeFilters.mentalHealthSupport?.requiresOHIP && !service.resources?.mental_health_support?.requires_ohip) {
                return false;
            }
            if (activeFilters.mentalHealthSupport?.requiresID && !service.resources?.mental_health_support?.requires_id) {
                return false;
            }

            // 性别要求过滤
            if (activeFilters.gender?.length > 0) {
                if (!activeFilters.gender.some(gender =>
                    service.eligibility?.gender?.includes(gender)
                )) {
                    return false;
                }
            }

            // 年龄要求过滤
            if (activeFilters.age?.length > 0) {
                if (!activeFilters.age.some(age =>
                    service.eligibility?.age?.includes(age)
                )) {
                    return false;
                }
            }

            // 群体政策过滤
            if (activeFilters.allowsFamilies && !service.eligibility?.groups?.families) {
                return false;
            }
            if (activeFilters.allowsImmigrants && !service.eligibility?.groups?.immigrants) {
                return false;
            }
            if (activeFilters.allowsLGBTQ && !service.eligibility?.groups?.lgbtq_plus) {
                return false;
            }
            if (activeFilters.allowsPets && !service.eligibility?.groups?.allows_pets) {
                return false;
            }
            if (activeFilters.allowsDrugs && !service.eligibility?.drug_policy?.allowed) {
                return false;
            }
            if (activeFilters.allowsAlcohol && !service.eligibility?.alcohol_policy?.allowed) {
                return false;
            }

            // 要求过滤
            if (activeFilters.hasFees && !service.fees?.required) {
                return false;
            }
            if (activeFilters.requiresDocuments && !service.documents_required?.required) {
                return false;
            }
            if (activeFilters.requiresApplication && !service.application_required?.required) {
                return false;
            }

            // 无障碍设施过滤
            if (activeFilters.isFullyAccessible && !service.accessibility?.fully_accessible) {
                return false;
            }

            return true;
        });
    }, [entities, activeFilters]);

    useEffect(() => {
        if (computedFilteredEntities.length > 0) {
            const sorted = [...computedFilteredEntities].sort((a, b) => {
                if (sortConfig.key === 'distance' && userPosition) {
                    const distanceA = calculateDistance(
                        userPosition[0],
                        userPosition[1],
                        a.address.latitude,
                        a.address.longitude
                    );
                    const distanceB = calculateDistance(
                        userPosition[0],
                        userPosition[1],
                        b.address.latitude,
                        b.address.longitude
                    );
                    return sortConfig.direction === 'asc' ? distanceA - distanceB : distanceB - distanceA;
                } else if (sortConfig.key === 'beds') {
                    const bedsA = a.services.find(s => s.type === "Shelter")?.resources?.beds?.beds_left || 0;
                    const bedsB = b.services.find(s => s.type === "Shelter")?.resources?.beds?.beds_left || 0;
                    return sortConfig.direction === 'asc' ? bedsA - bedsB : bedsB - bedsA;
                }
                return 0;
            });
            setFilteredEntities(sorted);
        } else {
            setFilteredEntities([]); // 如果没有过滤结果，设置为空数组
        }
    }, [sortConfig, userPosition, computedFilteredEntities]);

    const handleGetDirections = (destLat, destLng) => {
        if (!userPosition) return;
        const url = `https://www.google.com/maps/dir/?api=1&origin=${userPosition[0]},${userPosition[1]}&destination=${destLat},${destLng}&travelmode=walking`;
        window.open(url, '_blank');
    };

    const handleFilterApply = (filters) => {
        console.log('Applying filters:', filters);
        setActiveFilters(filters);
        setShowFilterModal(false);
    };

    const handleFilterClick = () => {
        setShowFilterModal(prev => !prev);
    };

    return (
        <>
            <div className="map-filter-container">
                <div className="map-container">
                    <MapContainer center={[position[0] - 0.05, position[1]]} zoom={12} className="map">
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {filteredEntities.map(entity => (
                            <Marker
                                key={entity.id + (selectedEntity && selectedEntity.id === entity.id ? "_selected" : "")}
                                position={[parseFloat(entity.address.latitude), parseFloat(entity.address.longitude)]}
                                icon={defaultIcon}
                            >
                                <Popup autoPan={true}>
                                    <div className="popup-content">
                                        <a onClick={() => handleEntityClick(entity.id)} className="popup-entity-name">
                                            {entity.entity_name}
                                        </a>
                                        <div className="popup-types">
                                            {entity.services?.map((service, index) => (
                                                service.sub_title && (
                                                    <div key={index} className="popup-type">
                                                        {service.sub_title}
                                                    </div>
                                                )
                                            )).filter(Boolean)}
                                        </div>
                                        <div className="popup-address">
                                            {entity.address.address_line_1}
                                            {entity.address.address_line_2 ? `, ${entity.address.address_line_2}` : ''}
                                        </div>
                                        <div className="popup-info-container">
                                            <div className="popup-info">
                                                <div className="popup-distance">
                                                    Distance: {userPosition ?
                                                    `${calculateDistance(
                                                        userPosition[0],
                                                        userPosition[1],
                                                        entity.address.latitude,
                                                        entity.address.longitude
                                                    )} km` : '-'}
                                                </div>
                                                {entity.services?.some(service => service.type === "Shelter") && (
                                                    <div className="popup-beds">
                                                        Beds
                                                        left: {entity.services.find(s => s.type === "Shelter")?.resources?.beds?.beds_left || 0}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="popup-directions">
                                                <button
                                                    onClick={() => handleGetDirections(
                                                        entity.address.latitude,
                                                        entity.address.longitude
                                                    )}
                                                    className="directions-button"
                                                >
                                                    <FaDirections/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                        {userPosition && (
                            <Marker position={userPosition} icon={defaultIcon}>
                                <Popup>You are here</Popup>
                            </Marker>
                        )}
                        {position && <MapCenter latitude={position[0]} longitude={position[1]} zoom={zoom}/>}
                    </MapContainer>
                </div>

                <div className="filter-button-container">
                    <button
                        className="filter-button"
                        onClick={handleFilterClick}
                    >
                        Filter
                    </button>
                </div>

                <div className="table-container">
                    <Table>
                        <thead>
                        <tr>
                            <th style={{width: '60%'}}>Entity Name</th>
                            <th
                                style={{width: '20%', cursor: 'pointer'}}
                                onClick={() => handleSort('distance')}
                                className="text-right"
                            >
                                Distance
                                {sortConfig.key === 'distance' ? (
                                    sortConfig.direction === 'asc' ? <GoTriangleUp/> : <GoTriangleDown/>
                                ) : <GoTriangleDown/>}
                            </th>
                            <th
                                style={{width: '20%', cursor: 'pointer'}}
                                onClick={() => handleSort('beds')}
                                className="text-right"
                            >
                                Beds Left
                                {sortConfig.key === 'beds' ? (
                                    sortConfig.direction === 'asc' ? <GoTriangleUp/> : <GoTriangleDown/>
                                ) : <GoTriangleDown/>}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredEntities.length > 0 ? (
                            filteredEntities.map(entity => {
                                const distance = userPosition ?
                                    `${calculateDistance(
                                        userPosition[0],
                                        userPosition[1],
                                        entity.address.latitude,
                                        entity.address.longitude
                                    )} km` : '-';

                                const shelterService = entity.services.find(s => s.type === "Shelter");
                                const bedsLeft = shelterService?.resources?.beds?.beds_left;
                                const bedsAvailable = shelterService?.resources?.beds?.available;

                                let bedsColor;
                                if (bedsAvailable && bedsLeft !== undefined) {
                                    if (bedsLeft > 100) bedsColor = 'var(--green)';
                                    else if (bedsLeft > 50) bedsColor = 'var(--yellow)';
                                    else bedsColor = 'var(--ruby)';
                                }

                                return (
                                    <tr key={entity.id}>
                                        <td style={{width: '60%'}}>
                                            <a
                                                onClick={() => handleEntityNameClick(entity.id)}
                                                style={{cursor: 'pointer'}}
                                            >
                                                {entity.entity_name}
                                            </a>
                                        </td>
                                        <td
                                            style={{width: '20%', cursor: 'pointer'}}
                                            className="text-right"
                                            onClick={() => handleTableEntityClick(
                                                parseFloat(entity.address.latitude),
                                                parseFloat(entity.address.longitude),
                                                entity
                                            )}
                                        >
                                            {distance}
                                        </td>
                                        <td
                                            style={{
                                                width: '20%',
                                                backgroundColor: bedsAvailable && bedsLeft !== undefined ? bedsColor : 'transparent'
                                            }}
                                            className="text-right"
                                        >
                                            {bedsAvailable && bedsLeft !== undefined ? bedsLeft : ''}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="3" className="text-center">No matching entities found</td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {showFilterModal && (
                <FilterModal
                    isOpen={showFilterModal}
                    onClose={() => setShowFilterModal(false)}
                    onApply={handleFilterApply}
                />
            )}
        </>
    );
};

export default Map;
